import React, { useState } from 'react';
import { MarginConfiguration, DEFAULT_MARGIN_CONFIG } from '@/types/admin';
import { LCBtn, CLS } from '@/components/common/LCPrimitives';
import LCIcon from '@/components/common/LCIcon';
import UserList from '@/components/AdminPanel/UserManagement/UserList';
import ARMAssignments from '@/components/AdminPanel/ARMAssignments/ARMAssignments';
import ICOverrides from '@/components/AdminPanel/ICOverrides/ICOverrides';
import MarginTradingOverridesTab from '@/components/AdminPanel/MarginTradingOverridesTab';
import EmailTemplates from '@/components/AdminPanel/EmailTemplates/EmailTemplates';

interface TabConfig {
  id: string;
  label: string;
  icon: string;
  description: string;
}

const TABS: TabConfig[] = [
  { id: 'user-management',         label: 'User Management',          icon: 'admin',    description: 'Manage user accounts, roles, and permissions' },
  { id: 'arm-assignments',         label: 'ARM Assignments',           icon: 'arrow',    description: 'Manage ARM to RM assignments' },
  { id: 'ic-overrides',            label: 'IC Overrides',              icon: 'check',    description: 'Override IC assignments for portfolios' },
  { id: 'email-templates',         label: 'Email Templates',           icon: 'mail',     description: 'Configure email templates for margin alerts' },
  { id: 'asset-types',             label: 'Asset Type Add-Ons',        icon: 'exposure', description: 'LC house add-ons to Pershing requirements by asset type' },
  { id: 'market-cap',              label: 'Market Cap & Volatility',   icon: 'dashboard',description: 'Market cap categories and volatility thresholds' },
  { id: 'concentration',           label: 'Concentration Rules',       icon: 'warn',     description: 'Add-ons for concentrated positions and portfolio limits' },
  { id: 'warning-thresholds',      label: 'Warning Thresholds',        icon: 'alerts',   description: 'Surplus cushion thresholds for status classification' },
  { id: 'margin-trading-overrides',label: 'Margin Trading Overrides',  icon: 'refresh',  description: 'Override automatic margin trading detection' },
];

const CONFIG_TABS = new Set(['asset-types', 'market-cap', 'concentration', 'warning-thresholds']);

/* ── Reusable primitives ─────────────────────────────────────── */

const Section: React.FC<{ title: string; sub?: string; children: React.ReactNode }> = ({ title, sub, children }) => (
  <div className="bg-white border border-black/[0.08] mb-4">
    <div className="px-5 py-3.5 border-b border-black/[0.06]">
      <div className="font-semibold text-[13.5px] text-black">{title}</div>
      {sub && <div className="text-[12px] text-[#7A7A76] mt-0.5">{sub}</div>}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const FieldGrid: React.FC<{ children: React.ReactNode; cols?: 2 | 1 }> = ({ children, cols = 2 }) => (
  <div className={`grid gap-4 ${cols === 2 ? 'grid-cols-2 max-[768px]:grid-cols-1' : 'grid-cols-1 max-w-sm'}`}>
    {children}
  </div>
);

interface FieldProps {
  label: string;
  hint?: string;
  value: number;
  onChange: (v: number) => void;
  prefix?: string;
  suffix?: string;
}

const Field: React.FC<FieldProps> = ({ label, hint, value, onChange, prefix, suffix }) => (
  <div className={CLS.field}>
    <label className={CLS.fieldLabel}>{label}</label>
    <div className="flex items-stretch border border-black/[0.16] bg-white focus-within:border-[#E50025] transition-colors">
      {prefix && (
        <span className="flex items-center px-3 text-[13px] text-[#7A7A76] border-r border-black/[0.10] bg-[#FAF9F8] shrink-0 select-none">
          {prefix}
        </span>
      )}
      <input
        type="number"
        className="flex-1 font-[inherit] text-[13.5px] font-light px-3 py-[9px] bg-transparent text-black focus:outline-none w-0 min-w-0"
        value={value}
        onChange={e => onChange(Number(e.target.value))}
      />
      {suffix && (
        <span className="flex items-center px-3 text-[13px] text-[#7A7A76] border-l border-black/[0.10] bg-[#FAF9F8] shrink-0 select-none">
          {suffix}
        </span>
      )}
    </div>
    {hint && <div className="text-[11px] text-[#7A7A76] mt-1">{hint}</div>}
  </div>
);

const InfoBanner: React.FC<{ children: React.ReactNode; variant?: 'info' | 'warn' }> = ({ children, variant = 'info' }) => (
  <div className={`flex items-start gap-2.5 px-4 py-3 text-[12.5px] leading-relaxed mt-2 ${
    variant === 'warn'
      ? 'bg-[rgba(255,191,0,0.10)] border border-[rgba(182,119,11,0.30)] text-[#b6770b]'
      : 'bg-[rgba(100,100,230,0.06)] border border-[rgba(100,100,230,0.25)] text-[#4444b0]'
  }`}>
    <LCIcon name={variant === 'warn' ? 'warn' : 'error'} size={14} className="shrink-0 mt-0.5" />
    <span>{children}</span>
  </div>
);

/* ── Tab content renderers ───────────────────────────────────── */

function AssetTypesTab({ config, onChange }: { config: MarginConfiguration; onChange: (s: keyof MarginConfiguration, f: string, v: number) => void }) {
  const ch = (f: string, v: number) => onChange('assetTypeAddOns', f, v);
  return (
    <>
      <Section title="Equity Categories" sub="Add-on percentages for different equity types based on market cap">
        <FieldGrid>
          <Field label="Large Cap Add-On" value={config.assetTypeAddOns.largeCap}   onChange={v => ch('largeCap', v)}   suffix="%" hint=">$10B market cap" />
          <Field label="Mid Cap Add-On"   value={config.assetTypeAddOns.midCap}     onChange={v => ch('midCap', v)}     suffix="%" hint="$2–10B market cap" />
          <Field label="Small Cap Add-On" value={config.assetTypeAddOns.smallCap}   onChange={v => ch('smallCap', v)}   suffix="%" hint="<$2B market cap" />
          <Field label="Volatile Stocks Add-On" value={config.assetTypeAddOns.volatile} onChange={v => ch('volatile', v)} suffix="%" hint=">40% 30-day volatility" />
        </FieldGrid>
        <FieldGrid cols={1}>
          <Field label="Penny Stocks Requirement" value={config.assetTypeAddOns.pennyStocks} onChange={v => ch('pennyStocks', v)} suffix="%" hint="<$5 per share (typically 100% — no margin)" />
        </FieldGrid>
      </Section>
      <Section title="Other Asset Types" sub="Add-on percentages for non-equity securities">
        <FieldGrid>
          <Field label="ETFs Add-On"           value={config.assetTypeAddOns.etfs}        onChange={v => ch('etfs', v)}        suffix="%" hint="All ETF types" />
          <Field label="Bonds Add-On"          value={config.assetTypeAddOns.bonds}       onChange={v => ch('bonds', v)}       suffix="%" hint="All bond types" />
          <Field label="Mutual Funds Add-On"   value={config.assetTypeAddOns.mutualFunds} onChange={v => ch('mutualFunds', v)} suffix="%" hint="Standard mutual funds" />
          <Field label="Options Add-On"        value={config.assetTypeAddOns.options}     onChange={v => ch('options', v)}     suffix="%" hint="Options strategies" />
        </FieldGrid>
        <FieldGrid cols={1}>
          <Field label="Other Securities Add-On" value={config.assetTypeAddOns.other} onChange={v => ch('other', v)} suffix="%" hint="Other marginable securities" />
        </FieldGrid>
      </Section>
    </>
  );
}

function MarketCapTab({ config, onChange }: { config: MarginConfiguration; onChange: (s: keyof MarginConfiguration, f: string, v: number) => void }) {
  const fmtNum = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v);
  return (
    <div className="grid grid-cols-2 gap-4 max-[768px]:grid-cols-1">
      <Section title="Market Cap Definitions" sub="Define thresholds for equity categorisation">
        <div className="flex flex-col gap-4">
          <Field label="Large Cap Threshold" value={config.marketCapDefinitions.largeCap} onChange={v => onChange('marketCapDefinitions', 'largeCap', v)} prefix="$" hint={`Currently: ${fmtNum(config.marketCapDefinitions.largeCap)}`} />
          <Field label="Mid Cap Lower Threshold" value={config.marketCapDefinitions.midCap} onChange={v => onChange('marketCapDefinitions', 'midCap', v)} prefix="$" hint={`Currently: ${fmtNum(config.marketCapDefinitions.midCap)}`} />
          <div className="text-[12px] text-[#7A7A76] mt-1">Small cap is defined as below {fmtNum(config.marketCapDefinitions.smallCap)}</div>
        </div>
      </Section>
      <Section title="Volatility & Price Thresholds" sub="Define risk classification thresholds">
        <div className="flex flex-col gap-4">
          <Field label="Volatile Stock Threshold" value={config.volatilityThresholds.volatileStock} onChange={v => onChange('volatilityThresholds', 'volatileStock', v)} suffix="%" hint="30-day annualised volatility threshold" />
          <Field label="Penny Stock Price Threshold" value={config.volatilityThresholds.pennyStock} onChange={v => onChange('volatilityThresholds', 'pennyStock', v)} prefix="$" hint="Below this price = penny stock" />
        </div>
      </Section>
    </div>
  );
}

function ConcentrationTab({ config, onChange }: { config: MarginConfiguration; onChange: (s: keyof MarginConfiguration, f: string, v: number) => void }) {
  const ch = (f: string, v: number) => onChange('concentrationAddOns', f, v);
  return (
    <div className="max-w-[640px]">
      <Section title="Concentration Add-Ons" sub="Additional requirements for concentrated positions">
        <div className="flex flex-col gap-4">
          <Field label="Single Position >30% Add-On"  value={config.concentrationAddOns.singlePosition30Percent} onChange={v => ch('singlePosition30Percent', v)} suffix="%" hint="Applied when a single position exceeds 30% of the portfolio" />
          <Field label="Single Position >50% Add-On"  value={config.concentrationAddOns.singlePosition50Percent} onChange={v => ch('singlePosition50Percent', v)} suffix="%" hint="Applied when a single position exceeds 50% of the portfolio" />
          <Field label="Top 3 Positions >70% Add-On"  value={config.concentrationAddOns.top3Positions70Percent}  onChange={v => ch('top3Positions70Percent', v)}  suffix="%" hint="Applied to concentrated positions when top 3 combined exceed 70%" />
          <InfoBanner>
            The higher of single position add-ons will be applied. The top 3 positions add-on applies to positions with individual weight greater than 20%.
          </InfoBanner>
        </div>
      </Section>
    </div>
  );
}

function WarningThresholdsTab({ config, onChange }: { config: MarginConfiguration; onChange: (s: keyof MarginConfiguration, f: string, v: number) => void }) {
  const ch = (f: string, v: number) => onChange('warningThresholds', f, v);
  return (
    <div className="max-w-[640px]">
      <Section title="Warning Thresholds" sub="Surplus cushion thresholds for alert generation">
        <FieldGrid>
          <Field label="Green Zone (Safe)"  value={config.warningThresholds.greenZone}      onChange={v => ch('greenZone', v)}      suffix="%" hint="Above this = Green status" />
          <Field label="Amber Alert Zone"   value={config.warningThresholds.amberAlertZone} onChange={v => ch('amberAlertZone', v)} suffix="%" hint="Down to this = Amber Alert status" />
          <Field label="Red Alert Zone"     value={config.warningThresholds.redAlertZone}   onChange={v => ch('redAlertZone', v)}   suffix="%" hint="Down to this = Red Alert status" />
          <Field label="Critical Zone"      value={config.warningThresholds.criticalZone}   onChange={v => ch('criticalZone', v)}   suffix="%" hint="At or below = Critical status" />
        </FieldGrid>
        <InfoBanner variant="warn">
          Critical alerts and potential liquidation procedures are triggered when a portfolio enters the Critical Zone.
        </InfoBanner>
      </Section>
    </div>
  );
}

/* ── Main component ──────────────────────────────────────────── */

const AdminPanel: React.FC = () => {
  const [config, setConfig] = useState<MarginConfiguration>(DEFAULT_MARGIN_CONFIG);
  const [isDirty, setIsDirty] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('user-management');

  const handleChange = (section: keyof MarginConfiguration, field: string, value: number) => {
    setConfig(prev => ({ ...prev, [section]: { ...(prev[section] as any), [field]: value } }));
    setIsDirty(true);
  };

  const handleSave = () => {
    setConfig(prev => ({ ...prev, lastUpdated: new Date(), updatedBy: 'Relationship Manager' }));
    setIsDirty(false);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 4000);
  };

  const handleReset = () => {
    setConfig(DEFAULT_MARGIN_CONFIG);
    setIsDirty(true);
  };

  const activeTabConfig = TABS.find(t => t.id === activeTab);
  const showActions = CONFIG_TABS.has(activeTab);

  const renderContent = () => {
    switch (activeTab) {
      case 'user-management':          return <UserList />;
      case 'arm-assignments':          return <ARMAssignments />;
      case 'ic-overrides':             return <ICOverrides />;
      case 'email-templates':          return <EmailTemplates />;
      case 'asset-types':              return <AssetTypesTab config={config} onChange={handleChange} />;
      case 'market-cap':               return <MarketCapTab config={config} onChange={handleChange} />;
      case 'concentration':            return <ConcentrationTab config={config} onChange={handleChange} />;
      case 'warning-thresholds':       return <WarningThresholdsTab config={config} onChange={handleChange} />;
      case 'margin-trading-overrides': return <MarginTradingOverridesTab />;
      default:                         return <UserList />;
    }
  };

  return (
    <div className="flex min-h-screen overflow-hidden">
      {/* Sidebar */}
      <aside className="w-[260px] min-w-[260px] bg-white border-r border-black/[0.08] flex flex-col sticky top-0 h-screen">
        <div className="px-4 pt-4 pb-3.5 border-b border-black/[0.06] shrink-0">
          <div className="text-[9.5px] font-bold tracking-[0.14em] uppercase text-[#E50025]">Admin Panel</div>
          <div className="text-[12px] text-[#7A7A76] mt-1">Select a category to configure</div>
        </div>
        <nav className="flex flex-col gap-px p-2.5 flex-1 overflow-y-auto">
          {TABS.map(tab => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-start gap-3 px-3 py-2.5 text-left w-full border-0 bg-transparent cursor-pointer rounded-sm relative transition-colors duration-[120ms] ${active ? 'bg-[#FAF9F8] text-[#E50025]' : 'text-[#333] hover:bg-[#FAF9F8] hover:text-black'}`}
              >
                {active && <span className="absolute left-0 top-2 bottom-2 w-0.5 bg-[#E50025]" />}
                <LCIcon name={tab.icon} size={14} className={`shrink-0 mt-0.5 ${active ? 'opacity-100' : 'opacity-60'}`} />
                <div className="min-w-0">
                  <div className={`text-[13px] leading-snug ${active ? 'font-semibold' : 'font-normal'}`}>{tab.label}</div>
                  <div className="text-[11px] text-[#7A7A76] mt-0.5 leading-snug">{tab.description}</div>
                </div>
              </button>
            );
          })}
        </nav>
        <div className="border-t border-black/[0.06] px-4 py-3 shrink-0">
          <div className="text-[10.5px] text-[#7A7A76]">
            Last saved: {config.lastUpdated.toLocaleString()}
          </div>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Content header */}
        <div className="bg-white border-b border-black/[0.08] px-6 py-4 flex items-start justify-between gap-4 shrink-0">
          <div>
            <h2 className="font-serif text-[26px] font-normal tracking-[-0.01em] leading-tight m-0">{activeTabConfig?.label}</h2>
            <div className="text-[13px] text-[#7A7A76] mt-1">{activeTabConfig?.description}</div>
          </div>
          {showActions && (
            <div className="flex gap-2 shrink-0 mt-1">
              <LCBtn variant="outline" size="sm" onClick={handleReset}>
                <LCIcon name="refresh" size={12} /> Reset to Defaults
              </LCBtn>
              <LCBtn variant="primary" size="sm" onClick={handleSave} disabled={!isDirty}>
                <LCIcon name="check" size={12} /> Save Configuration
              </LCBtn>
            </div>
          )}
        </div>

        {/* Unsaved changes banner */}
        {isDirty && showActions && (
          <div className="flex items-center gap-2.5 px-6 py-2.5 text-[12.5px] font-medium text-[#b6770b] bg-[rgba(255,191,0,0.10)] border-b border-[rgba(182,119,11,0.28)] shrink-0">
            <LCIcon name="warn" size={14} className="shrink-0" />
            Unsaved changes — save before switching tabs or leaving this page.
          </div>
        )}

        {/* Save success banner */}
        {saveSuccess && (
          <div className="flex items-center gap-2.5 px-6 py-2.5 text-[12.5px] font-medium text-[#1f8a4a] bg-[rgba(31,138,74,0.08)] border-b border-[rgba(31,138,74,0.28)] shrink-0">
            <LCIcon name="check" size={14} className="shrink-0" />
            Configuration saved successfully.
          </div>
        )}

        {/* Tab content */}
        <div className="flex-1 overflow-auto p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
